import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiMail, 
  FiLock, 
  FiUser,
  FiCheck,
  FiAlertCircle,
  FiEye,
  FiEyeOff
} from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook } from 'react-icons/fa';
import './Inscription.css';
import { authAPI } from '../../api';

const Inscription = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        password_confirmation: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        
        if (name === 'password_confirmation' && formData.password === value) {
            setErrors(prev => ({ ...prev, password_confirmation: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Le nom est requis';
        } else if (formData.name.length < 2) {
            newErrors.name = 'Le nom doit contenir au moins 2 caractères';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = 'L\'email est requis';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Format d\'email invalide';
        }
        
        if (!formData.password) {
            newErrors.password = 'Le mot de passe est requis';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
            newErrors.password = 'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre';
        }
        
        if (!formData.password_confirmation) {
            newErrors.password_confirmation = 'La confirmation du mot de passe est requise';
        } else if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
        }
        
        if (!acceptedTerms) {
            newErrors.terms = 'Vous devez accepter les conditions d\'utilisation';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        try {
            setLoading(true);
            
            const response = await authAPI.register({
                name: formData.name,
                email: formData.email,
                password: formData.password,
                password_confirmation: formData.password_confirmation
            });
            
            if (response.success) {
                localStorage.setItem('token', response.token);
                localStorage.setItem('user', JSON.stringify(response.user));
                localStorage.setItem('userRole', response.user.role || 'user');
                
                navigate('/');
                alert('Inscription réussie ! Bienvenue !');
            } else {
                throw new Error(response.message || 'Erreur d\'inscription');
            }
        } catch (error) {
            console.error('Erreur d\'inscription:', error);
            
            if (error.status === 422 && error.errors) {
                setErrors(error.errors);
            } else if (error.status === 400) {
                setErrors({
                    general: 'Un utilisateur avec cet email existe déjà'
                });
            } else {
                setErrors({
                    general: error.message || 'Une erreur est survenue lors de l\'inscription'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const checkPasswordRequirement = (requirement) => {
        const password = formData.password;
        if (!password) return false;
        
        switch(requirement) {
            case 'length':
                return password.length >= 8;
            case 'lowercase':
                return /[a-z]/.test(password);
            case 'uppercase':
                return /[A-Z]/.test(password);
            case 'number':
                return /\d/.test(password);
            case 'special':
                return /[^A-Za-z0-9]/.test(password);
            default:
                return false;
        }
    };

    const passwordStrength = () => {
        const password = formData.password;
        if (!password) return { score: 0, text: '', color: '#e74c3c' };
        
        let score = 0;
        if (password.length >= 8) score++;
        if (/[a-z]/.test(password)) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;
        
        const strength = {
            0: { text: 'Très faible', color: '#dc143c' },
            1: { text: 'Faible', color: '#dc143c' },
            2: { text: 'Moyen', color: '#f39c12' },
            3: { text: 'Bon', color: '#f1c40f' },
            4: { text: 'Fort', color: '#2ecc71' },
            5: { text: 'Très fort', color: '#27ae60' }
        };
        
        return { score, ...strength[score] };
    };

    const strength = passwordStrength();

    return (
        <div className="inscription-container">
            {/* Effets de fond */}
            <div className="background-shapes">
                <div className="shape shape-1"></div>
                <div className="shape shape-2"></div>
                <div className="shape shape-3"></div>
                <div className="shape shape-4"></div>
                <div className="shape shape-5"></div>
            </div>

            {/* Lien de retour */}
            <Link to="/" className="back-to-homeinscription">
                <FiArrowLeft className="back-iconinscription" />
                <span>Retour à l'accueil</span>
            </Link>

            <div className="inscription-wrapper">
                {/* Section gauche - Formulaire */}
                <div className="inscription-form-section">
                    <div className="form-container">
                        <div className="form-header">
                            <h2 className="form-title">Créer un compte</h2>
                            <p className="form-subtitle">
                                Rejoignez ETEF Connect pour débuter votre aventure en France
                            </p>
                        </div>

                        {errors.general && (
                            <div className="error-message-container">
                                <FiAlertCircle className="error-icon" />
                                <span>{errors.general}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="inscription-form">
                            <div className="form-group">
                                <div className="input-group">
                                    <div className="input-icon">
                                        <FiUser />
                                    </div>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={errors.name ? 'input-error' : ''}
                                        placeholder="Nom complet"
                                        disabled={loading}
                                    />
                                    <label className="floating-label">Nom complet</label>
                                </div>
                                {errors.name && <span className="field-error">{errors.name}</span>}
                            </div>

                            <div className="form-group">
                                <div className="input-group">
                                    <div className="input-icon">
                                        <FiMail />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={errors.email ? 'input-error' : ''}
                                        placeholder="Adresse email"
                                        disabled={loading}
                                    />
                                    <label className="floating-label">Email</label>
                                </div>
                                {errors.email && <span className="field-error">{errors.email}</span>}
                            </div>

                            <div className="form-group">
                                <div className="input-group">
                                    <div className="input-icon">
                                        <FiLock />
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className={errors.password ? 'input-error' : ''}
                                        placeholder="Mot de passe"
                                        disabled={loading}
                                    />
                                    <button 
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <FiEyeOff /> : <FiEye />}
                                    </button>
                                    <label className="floating-label">Mot de passe</label>
                                </div>
                                
                                {formData.password && (
                                    <div className="password-strength">
                                        <div className="strength-meter">
                                            {[1, 2, 3, 4, 5].map((level) => (
                                                <div 
                                                    key={level}
                                                    className={`strength-bar ${level <= strength.score ? 'filled' : ''}`}
                                                    style={{ backgroundColor: level <= strength.score ? strength.color : '#e2e8f0' }}
                                                ></div>
                                            ))}
                                        </div>
                                        <span 
                                            className="strength-text"
                                            style={{ color: strength.color }}
                                        >
                                            {strength.text}
                                        </span>
                                    </div>
                                )}
                                
                                {errors.password && <span className="field-error">{errors.password}</span>}
                                
                                <div className="password-requirements">
                                    <p className="requirements-title">Le mot de passe doit contenir :</p>
                                    <ul className="requirements-list">
                                        <li className={checkPasswordRequirement('length') ? 'valid' : ''}>
                                            <FiCheck className="requirement-icon" />
                                            Au moins 8 caractères
                                        </li>
                                        <li className={checkPasswordRequirement('lowercase') ? 'valid' : ''}>
                                            <FiCheck className="requirement-icon" />
                                            Une lettre minuscule
                                        </li>
                                        <li className={checkPasswordRequirement('uppercase') ? 'valid' : ''}>
                                            <FiCheck className="requirement-icon" />
                                            Une lettre majuscule
                                        </li>
                                        <li className={checkPasswordRequirement('number') ? 'valid' : ''}>
                                            <FiCheck className="requirement-icon" />
                                            Un chiffre
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="form-group">
                                <div className="input-group">
                                    <div className="input-icon">
                                        <FiLock />
                                    </div>
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="password_confirmation"
                                        value={formData.password_confirmation}
                                        onChange={handleChange}
                                        className={errors.password_confirmation ? 'input-error' : ''}
                                        placeholder="Confirmer le mot de passe"
                                        disabled={loading}
                                    />
                                    <button 
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                                    </button>
                                    <label className="floating-label">Confirmation</label>
                                </div>
                                {errors.password_confirmation && (
                                    <span className="field-error">{errors.password_confirmation}</span>
                                )}
                            </div>

                            <div className="form-group terms-group">
                                <label className="checkbox-container">
                                    <input
                                        type="checkbox"
                                        checked={acceptedTerms}
                                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                                        disabled={loading}
                                    />
                                    <span className="checkmark"></span>
                                    <span className="checkbox-label">
                                        J'accepte les{' '}
                                        <Link to="/conditions" className="terms-link">
                                            conditions d'utilisation
                                        </Link>
                                        {' '}et la{' '}
                                        <Link to="/confidentialite" className="terms-link">
                                            politique de confidentialité
                                        </Link>
                                    </span>
                                </label>
                                {errors.terms && <span className="field-error">{errors.terms}</span>}
                            </div>

                            <button 
                                type="submit" 
                                className="submit-button"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="loading-spinnerhdhd">
                                        <div className="spinnerdhd"></div>
                                        <span>Création du compte...</span>
                                    </div>
                                ) : (
                                    'Créer mon compte'
                                )}
                            </button>

                            <div className="divider">
                                <span className="divider-text">Ou continuer avec</span>
                            </div>

                            <div className="social-signup">
                                <button type="button" className="social-button google">
                                    <FcGoogle className="social-icon" />
                                    <span>Google</span>
                                </button>
                                <button type="button" className="social-button facebook">
                                    <FaFacebook className="social-icon" />
                                    <span>Facebook</span>
                                </button>
                            </div>

                            <div className="form-footer">
                                <p className="login-link">
                                    Vous avez déjà un compte ?{' '}
                                    <Link to="/connexion" className="link">
                                        Se connecter
                                    </Link>
                                </p>
                                <p className="terms-note">
                                    En créant un compte, vous acceptez nos{' '}
                                    <Link to="/conditions" className="link">Conditions d'utilisation</Link>{' '}
                                    et notre{' '}
                                    <Link to="/confidentialite" className="link">Politique de confidentialité</Link>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Section droite - Avantages */}
                <div className="inscription-benefits">
                    <div className="benefits-content">
                        <div className="benefits-header">
                            <div className="benefits-icon">
                                <div className="icon-wrapper">
                                    <FiCheck className="check-icon" />
                                </div>
                            </div>
                            <h1 className="benefits-title">
                                Rejoignez la communauté<br/><span className="highlight">ETEF</span>
                            </h1>
                            <p className="benefits-subtitle">
                                Accédez à des avantages exclusifs pour votre parcours d'études en France
                            </p>
                        </div>

                        <div className="benefits-list">
                            <div className="benefit-item">
                                <div className="benefit-icon">
                                    <FiCheck />
                                </div>
                                <div className="benefit-text">
                                    <h3>Suivi personnalisé</h3>
                                    <p>Accompagnement individuel pour votre projet d'études</p>
                                </div>
                            </div>

                            <div className="benefit-item">
                                <div className="benefit-icon">
                                    <FiCheck />
                                </div>
                                <div className="benefit-text">
                                    <h3>Documentation complète</h3>
                                    <p>Accès à tous les guides et ressources nécessaires</p>
                                </div>
                            </div>

                            <div className="benefit-item">
                                <div className="benefit-icon">
                                    <FiCheck />
                                </div>
                                <div className="benefit-text">
                                    <h3>Support prioritaire</h3>
                                    <p>Assistance rapide pour vos démarches administratives</p>
                                </div>
                            </div>

                            <div className="benefit-item">
                                <div className="benefit-icon">
                                    <FiCheck />
                                </div>
                                <div className="benefit-text">
                                    <h3>Réseau d'étudiants</h3>
                                    <p>Échangez avec d'autres étudiants internationaux</p>
                                </div>
                            </div>
                        </div>

                        <div className="testimonial">
                            <p className="testimonial-text">
                                "Grâce à ETEF, j'ai pu réaliser mon rêve d'étudier en France. 
                                L'accompagnement est exceptionnel !"
                            </p>
                            <p className="testimonial-author">- Marie, étudiante ivoirienne</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Inscription;